<template>
  <article>
    <activity-stage :customRootContainerClass="'painted-finger-clue'">
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content">
        <!-- eslint-disable vue/no-parsing-error -->
        <figure
          :key="currentKey"
          class="keyboard-k2 painted-finger-clue-keys focusable"
          :style="{ margin: `0px 0px 0px ${marginLeftCurrentKey}px` }"
          tabindex="0"
        >
          <div :class="currentKey" :style="{ backgroundColor: currentKeyColor }"></div>
        </figure>

        <div
          class="painted-finger-clue-container"
          :class="!showBothHands ? 'painted-one-hand' : ''"
          :style="{
            visibility: progress && (showBothHands || holdKeyPressed) ? 'visible' : 'hidden'
          }"
        >
          <div v-if="isVisibleLeftHand" class="painted-finger-clue-left-side">
            <div class="svg-image-container current-letter">
              <svg width="200px" v-html="leftHand.svg"></svg>
            </div>
          </div>

          <div v-if="isVisibleRightHand" class="painted-finger-clue-right-side">
            <div class="svg-image-container current-letter">
              <svg width="200px" v-html="rightHand.svg"></svg>
            </div>
          </div>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import TouchAndHoldHelper from 'mixins/TouchAndHoldHelper';
import ErrorLogger from 'mixins/ErrorLogger';

import { convertSymbolToName } from 'utils/key-util';
import { mapState } from 'vuex';

// Import left hand images
import LHEmpty from 'assets/svg/target-practice/hand-pfc-left-empty.svg';

// Row0
import LHPinkyOrange from 'assets/svg/target-practice/left-hand/left-hand-pinky-orange.svg';
import LHRingOrange from 'assets/svg/target-practice/left-hand/left-hand-ring-orange.svg';
import LHMiddleOrange from 'assets/svg/target-practice/left-hand/left-hand-middle-orange.svg';
import LHIndexOrange from 'assets/svg/target-practice/left-hand/left-hand-index-orange.svg';
import LHIndexExtraOrange from 'assets/svg/target-practice/extra-finger-lh/extra-finger-lh-orange.svg';

// Row1
import LHPinkyYellow from 'assets/svg/target-practice/left-hand/left-hand-pinky-yellow.svg';
import LHRingYellow from 'assets/svg/target-practice/left-hand/left-hand-ring-yellow.svg';
import LHMiddleYellow from 'assets/svg/target-practice/left-hand/left-hand-middle-yellow.svg';
import LHIndexYellow from 'assets/svg/target-practice/left-hand/left-hand-index-yellow.svg';
import LHIndexExtraYellow from 'assets/svg/target-practice/extra-finger-lh/extra-finger-lh-yellow.svg';

// Row2
import LHPinkyGreen from 'assets/svg/target-practice/left-hand/left-hand-pinky-green.svg';
import LHRingGreen from 'assets/svg/target-practice/left-hand/left-hand-ring-green.svg';
import LHMiddleGreen from 'assets/svg/target-practice/left-hand/left-hand-middle-green.svg';
import LHIndexGreen from 'assets/svg/target-practice/left-hand/left-hand-index-green.svg';
import LHIndexExtraGreen from 'assets/svg/target-practice/extra-finger-lh/extra-finger-lh-green.svg';

// Row3
import LHPinkyBlue from 'assets/svg/target-practice/left-hand/left-hand-pinky-blue.svg';
import LHRingBlue from 'assets/svg/target-practice/left-hand/left-hand-ring-blue.svg';
import LHMiddleBlue from 'assets/svg/target-practice/left-hand/left-hand-middle-blue.svg';
import LHIndexBlue from 'assets/svg/target-practice/left-hand/left-hand-index-blue.svg';
import LHIndexExtraBlue from 'assets/svg/target-practice/extra-finger-lh/extra-finger-lh-blue.svg';

// Import right hand images
import RHEmpty from 'assets/svg/target-practice/hand-pfc-right-empty.svg';

// Row0
import RHIndexOrange from 'assets/svg/target-practice/right-hand/right-hand-index-orange.svg';
import RHMiddleOrange from 'assets/svg/target-practice/right-hand/right-hand-middle-orange.svg';
import RHRingOrange from 'assets/svg/target-practice/right-hand/right-hand-ring-orange.svg';
import RHPinkyOrange from 'assets/svg/target-practice/right-hand/right-hand-pinky-orange.svg';
import RHIndexExtraOrange from 'assets/svg/target-practice/extra-finger-rh/extra-finger-rh-orange.svg';

// Row1
import RHIndexYellow from 'assets/svg/target-practice/right-hand/right-hand-index-yellow.svg';
import RHMiddleYellow from 'assets/svg/target-practice/right-hand/right-hand-middle-yellow.svg';
import RHRingYellow from 'assets/svg/target-practice/right-hand/right-hand-ring-yellow.svg';
import RHPinkyYellow from 'assets/svg/target-practice/right-hand/right-hand-pinky-yellow.svg';
import RHIndexExtraYellow from 'assets/svg/target-practice/extra-finger-rh/extra-finger-rh-yellow.svg';

// Row2
import RHIndexGreen from 'assets/svg/target-practice/right-hand/right-hand-index-green.svg';
import RHMiddleGreen from 'assets/svg/target-practice/right-hand/right-hand-middle-green.svg';
import RHRingGreen from 'assets/svg/target-practice/right-hand/right-hand-ring-green.svg';
import RHPinkyGreen from 'assets/svg/target-practice/right-hand/right-hand-pinky-green.svg';
import RHIndexExtraGreen from 'assets/svg/target-practice/extra-finger-rh/extra-finger-rh-green.svg';

// Row3
import RHIndexBlue from 'assets/svg/target-practice/right-hand/right-hand-index-blue.svg';
import RHMiddleBlue from 'assets/svg/target-practice/right-hand/right-hand-middle-blue.svg';
import RHRingBlue from 'assets/svg/target-practice/right-hand/right-hand-ring-blue.svg';
import RHPinkyBlue from 'assets/svg/target-practice/right-hand/right-hand-pinky-blue.svg';
import RHIndexExtraBlue from 'assets/svg/target-practice/extra-finger-rh/extra-finger-rh-blue.svg';

const lettersQueues = {
  nrow: '`1234567890-=',
  qrow: 'qwertyuiop[]',
  arow: "asdfghjkl;'\\",
  zrow: 'zxcvbnm,./'
};

const targetColors = {
  nrow: '#FAD4A4',
  qrow: '#FFF6CB',
  arow: '#DFEBBE',
  zrow: '#D2E6EE',
  rowSpace: '#d4d1e3'
};

import ActivityStage from 'components/activity/ActivityStage.vue';

export default {
  components: {
    ActivityStage
  },
  mixins: [ActivityManager, TypingActivityHelper, TouchAndHoldHelper, ErrorLogger],
  data() {
    const marginsLeftForCurrentKey = {
      LHEmpty: 40,
      LHPinky: 155,
      LHRing: 220,
      LHMiddle: 280,
      LHIndex: 330,

      RHEmpty: 40,
      RHIndex: 560,
      RHMiddle: 610,
      RHRing: 660,
      RHPinky: 730
    };

    return {
      activityName: 'PaintedFingerClues',
      showBothHands: false,
      displayHand: '',
      currentKey: undefined,
      touchAndHold: false,
      needsSwitch: false,
      marginsLeftForCurrentKey,
      hands: {
        left: {
          images: {
            1: {
              svg: LHPinkyOrange,
              marginLeftCurrentKey: marginsLeftForCurrentKey.LHPinky,
              description: 'Left pinky finger'
            },
            2: {
              svg: LHRingOrange,
              marginLeftCurrentKey: marginsLeftForCurrentKey.LHRing,
              description: 'Left ring finger'
            },
            3: {
              svg: LHMiddleOrange,
              marginLeftCurrentKey: marginsLeftForCurrentKey.LHMiddle,
              description: 'Left middle finger'
            },
            4: {
              svg: LHIndexOrange,
              marginLeftCurrentKey: marginsLeftForCurrentKey.LHIndex,
              description: 'Left index finger'
            },
            5: {
              svg: LHIndexExtraOrange,
              marginLeftCurrentKey: marginsLeftForCurrentKey.LHIndex,
              description: 'Left index finger'
            },
            q: {
              svg: LHPinkyYellow,
              marginLeftCurrentKey: marginsLeftForCurrentKey.LHPinky,
              description: 'Left pinky finger'
            },
            w: {
              svg: LHRingYellow,
              marginLeftCurrentKey: marginsLeftForCurrentKey.LHRing,
              description: 'Left ring finger'
            },
            e: {
              svg: LHMiddleYellow,
              marginLeftCurrentKey: marginsLeftForCurrentKey.LHMiddle,
              description: 'Left middle finger'
            },
            r: {
              svg: LHIndexYellow,
              marginLeftCurrentKey: marginsLeftForCurrentKey.LHIndex,
              description: 'Left index finger'
            },
            t: {
              svg: LHIndexExtraYellow,
              marginLeftCurrentKey: marginsLeftForCurrentKey.LHIndex,
              description: 'Left index finger'
            },
            a: {
              svg: LHPinkyGreen,
              marginLeftCurrentKey: marginsLeftForCurrentKey.LHPinky,
              description: 'Left pinky finger'
            },
            s: {
              svg: LHRingGreen,
              marginLeftCurrentKey: marginsLeftForCurrentKey.LHRing,
              description: 'Left ring finger'
            },
            d: {
              svg: LHMiddleGreen,
              marginLeftCurrentKey: marginsLeftForCurrentKey.LHMiddle,
              description: 'Left middle finger'
            },
            f: {
              svg: LHIndexGreen,
              marginLeftCurrentKey: marginsLeftForCurrentKey.LHIndex,
              description: 'Left index finger'
            },
            g: {
              svg: LHIndexExtraGreen,
              marginLeftCurrentKey: marginsLeftForCurrentKey.LHIndex,
              description: 'Left index finger'
            },
            z: {
              svg: LHPinkyBlue,
              marginLeftCurrentKey: marginsLeftForCurrentKey.LHPinky,
              description: 'Left pinky finger'
            },
            x: {
              svg: LHRingBlue,
              marginLeftCurrentKey: marginsLeftForCurrentKey.LHRing,
              description: 'Left ring finger'
            },
            c: {
              svg: LHMiddleBlue,
              marginLeftCurrentKey: marginsLeftForCurrentKey.LHMiddle,
              description: 'Left middle finger'
            },
            v: {
              svg: LHIndexBlue,
              marginLeftCurrentKey: marginsLeftForCurrentKey.LHIndex,
              description: 'Left index finger'
            },
            b: {
              svg: LHIndexExtraBlue,
              marginLeftCurrentKey: marginsLeftForCurrentKey.LHIndex,
              description: 'Left index finger'
            },
            empty: {
              svg: LHEmpty,
              marginLeftCurrentKey: marginsLeftForCurrentKey.LHEmpty,
              description: 'Left hand'
            }
          }
        },
        right: {
          images: {
            6: {
              svg: RHIndexExtraOrange,
              marginLeftCurrentKey: marginsLeftForCurrentKey.RHIndex,
              description: 'Right index finger'
            },
            7: {
              svg: RHIndexOrange,
              marginLeftCurrentKey: marginsLeftForCurrentKey.RHIndex,
              description: 'Right index finger'
            },
            8: {
              svg: RHMiddleOrange,
              marginLeftCurrentKey: marginsLeftForCurrentKey.RHMiddle,
              description: 'Right middle finger'
            },
            9: {
              svg: RHRingOrange,
              marginLeftCurrentKey: marginsLeftForCurrentKey.RHRing,
              description: 'Right ring finger'
            },
            0: {
              svg: RHPinkyOrange,
              marginLeftCurrentKey: marginsLeftForCurrentKey.RHPinky,
              description: 'Right pinky finger'
            },
            y: {
              svg: RHIndexExtraYellow,
              marginLeftCurrentKey: marginsLeftForCurrentKey.RHIndex,
              description: 'Right index finger'
            },
            u: {
              svg: RHIndexYellow,
              marginLeftCurrentKey: marginsLeftForCurrentKey.RHIndex,
              description: 'Right index finger'
            },
            i: {
              svg: RHMiddleYellow,
              marginLeftCurrentKey: marginsLeftForCurrentKey.RHMiddle,
              description: 'Right middle finger'
            },
            o: {
              svg: RHRingYellow,
              marginLeftCurrentKey: marginsLeftForCurrentKey.RHRing,
              description: 'Right ring finger'
            },
            p: {
              svg: RHPinkyYellow,
              marginLeftCurrentKey: marginsLeftForCurrentKey.RHPinky,
              description: 'Right pinky finger'
            },
            h: {
              svg: RHIndexExtraGreen,
              marginLeftCurrentKey: marginsLeftForCurrentKey.RHIndex,
              description: 'Right index finger'
            },
            j: {
              svg: RHIndexGreen,
              marginLeftCurrentKey: marginsLeftForCurrentKey.RHIndex,
              description: 'Right index finger'
            },
            k: {
              svg: RHMiddleGreen,
              marginLeftCurrentKey: marginsLeftForCurrentKey.RHMiddle,
              description: 'Right middle finger'
            },
            l: {
              svg: RHRingGreen,
              marginLeftCurrentKey: marginsLeftForCurrentKey.RHRing,
              description: 'Right ring finger'
            },
            ';': {
              svg: RHPinkyGreen,
              marginLeftCurrentKey: marginsLeftForCurrentKey.RHPinky,
              description: 'Right pinky finger'
            },
            n: {
              svg: RHIndexExtraBlue,
              marginLeftCurrentKey: marginsLeftForCurrentKey.RHIndex,
              description: 'Right index finger'
            },
            m: {
              svg: RHIndexBlue,
              marginLeftCurrentKey: marginsLeftForCurrentKey.RHIndex,
              description: 'Right index finger'
            },
            ',': {
              svg: RHMiddleBlue,
              marginLeftCurrentKey: marginsLeftForCurrentKey.RHMiddle,
              description: 'Right middle finger'
            },
            '.': {
              svg: RHRingBlue,
              marginLeftCurrentKey: marginsLeftForCurrentKey.RHRing,
              description: 'Right ring finger'
            },
            '/': {
              svg: RHPinkyBlue,
              marginLeftCurrentKey: marginsLeftForCurrentKey.RHPinky,
              description: 'Right pinky finger'
            },
            empty: {
              svg: RHEmpty,
              marginLeftCurrentKey: marginsLeftForCurrentKey.RHEmpty,
              description: 'Right hand'
            }
          }
        }
      }
    };
  },

  computed: {
    ...mapState('keyboard', ['showSpecialKeys']),

    marginLeftCurrentKey() {
      let marginLeft = this.marginsLeftForCurrentKey.LHEmpty;
      if (this.isVisibleLeftHand) {
        // eslint-disable-next-line prefer-destructuring
        marginLeft = this.leftHand.marginLeftCurrentKey;
      }

      if (
        this.isVisibleRightHand &&
        this.rightHand.marginLeftCurrentKey !== this.marginsLeftForCurrentKey.LHEmpty
      ) {
        // eslint-disable-next-line prefer-destructuring
        marginLeft = this.rightHand.marginLeftCurrentKey;
      }

      if (!marginLeft) {
        marginLeft = this.marginsLeftForCurrentKey.LHEmpty;
      }
      return marginLeft;
    },

    isVisibleLeftHand() {
      return this.showHand('left');
    },

    leftHand() {
      return this.getHand('left', this.expectedKey);
    },

    isVisibleRightHand() {
      return this.showHand('right');
    },

    rightHand() {
      return this.getHand('right', this.expectedKey);
    },

    currentKeyColor() {
      if (this.currentStep === ' ') return targetColors.rowSpace;
      if (this.currentKey === 'keyabc switchKey' || this.currentKey === 'key123 switchKey')
        return targetColors.rowSpace;

      let keyColor = '';

      Object.entries(lettersQueues).some(([key, value]) => {
        if ([...value].includes(this.currentStep)) {
          keyColor = key;
          return true;
        }
        return false;
      });

      return targetColors[keyColor];
    }
  },

  methods: {
    async loadActivity() {
      const roundSetup = this.getActivityState();
      this.createRounds(roundSetup);
    },

    getKeyboardConfig() {
      const { letters } = this.rounds[0];
      const hideNumberRow = !this.activityData.keyboard.showNumbers;
      const { keyboardType, roofs, characters } = this.activityData.keyboard;

      const containsNumbers = /\d/.test(letters);
      const showTargets = !(this.ipadWithoutExternalKeyboard && !!characters);

      const keyboardConfig = {
        showTargets,
        keys:
          this.isMobile && containsNumbers && !this.userDevice.usingExternalKeyboard
            ? null
            : letters,
        isTouchHold:
          this.isMobile && containsNumbers && !this.userDevice.usingExternalKeyboard
            ? false
            : this.touchAndHold,
        hideNumberRow,
        keyboardType,
        roofs
      };

      return keyboardConfig;
    },

    getActivityState() {
      const { rounds } = this.activityData;
      const { characters } = this.activityData.keyboard;

      const containsNumbers = characters && /\d/.test(characters);

      this.touchAndHold =
        this.isMobile && containsNumbers && !this.userDevice.usingExternalKeyboard
          ? false
          : characters.includes('F') !== characters.includes('J');

      return {
        rounds,
        stepIndicator: 'keystrokes',
        instructions: this.getActivityInstruction(characters, this.touchAndHold)
      };
    },

    stepCallback() {
      return this.currentStep;
    },

    startActivity() {
      this.setupNewRound({ stepCallback: this.stepCallback });
      this.enableKeyboard();
      this.errorLoggerStart();

      this.$nextTick(this.setActivityContainerHeight);

      if (this.touchAndHold) {
        this.displayHand = this.targetKey === 'j' ? 'left' : 'right';
      } else {
        this.showBothHands = true;
        this.displayHand = '';
      }

      this.currentKey = this.getCurrentKey();
    },

    getActivityInstruction(letters, isTouchHold) {
      const containsNumbers = letters && /\d/.test(letters);
      const containsLetters = letters && /[a-z]/i.test(letters);
      let voice = '';
      let written = '';

      if (containsLetters && !containsNumbers) {
        voice = `paintedfingerclues${isTouchHold ? '' : 'nth'}-${this.getRandomVoice()}-1.mp3`;
        written = isTouchHold
          ? 'Hold the target. Then type the letters at the top.'
          : 'Type the letters at the top.';
      }
      if (containsNumbers && !containsLetters) {
        voice = `paintedfingerclues${isTouchHold ? '' : 'nth'}-fa-2.mp3`;
        written = isTouchHold
          ? 'Hold the target. Then type the numbers at the top.'
          : 'Type the numbers at the top.';
      }
      if (containsNumbers && containsLetters) {
        voice = `paintedfingerclues${isTouchHold ? '' : 'nth'}-fa-3.mp3`;
        written = isTouchHold
          ? 'Hold the target. Then type the letters, or numbers at the top.'
          : 'Type the letters, or numbers at the top.';
      }
      return {
        voice: `${this.voiceInstructionsPrefix}/${voice}`,
        written
      };
    },

    showHand(side) {
      return this.showBothHands || this.displayHand === side;
    },

    getHand(side, key) {
      const handImages = this.hands[side].images;
      if (this.needsSwitch) return handImages.empty;
      return handImages[key] || handImages.empty;
    },

    getCurrentKey() {
      let currentKey = '';
      const { currentStep } = this;

      const key =
        `key${convertSymbolToName(currentStep)}` || `special-${convertSymbolToName(currentStep)}`;

      const keyElement = document.getElementsByClassName(key)[0];

      if (keyElement) {
        this.needsSwitch = false;
        currentKey = keyElement.classList.value;
      }

      if (key && !keyElement) {
        this.needsSwitch = true;
        currentKey = `${/\d/.test(key) ? 'key123' : 'keyabc'} switchKey`;
      }

      return `${currentKey}${
        this.marginLeftCurrentKey > 40 && this.marginLeftCurrentKey <= 330 ? ' leftHandKey' : ''
      }${this.marginLeftCurrentKey > 40 && this.marginLeftCurrentKey > 330 ? ' rightHandKey' : ''}`;
    },

    handleCorrectKey(attempt) {
      if (this.isLastStepInRound) {
        this.finishRound();
        return;
      }

      this.sndType();
      this.captureKeyboardAttemptSegment(attempt, true);

      this.setCurrentStep({ stepCallback: this.stepCallback });
      this.$nextTick(() => {
        this.currentKey = this.getCurrentKey();
      });
    },

    finishRound() {
      this.sndBell();

      if (this.isLastRound) {
        this.completeActivity();
      } else {
        this.startRound();
      }
    }
  },

  watch: {
    expectedKey(newKey) {
      let instructions = `Type the ${newKey} key.`;

      this.setCurrentKeyboardingSegment('');
      this.setTotalKeyboardingSegment(newKey);
      this.setScreenReaderAlert(this.getTouchHoldBasedInstructions(instructions));
    },
    showSpecialKeys() {
      this.needsSwitch = false;
      this.$nextTick(() => {
        this.currentKey = this.getCurrentKey();
      });
    }
  }
};
</script>

<style lang="scss">
@import '../assets/sass/keyboards/partials/keyboardk2art.scss';
@import '../assets/sass/_zoom-reflow.scss';

.keyboard-k2 .switchKey {
  padding-top: 48%;
  height: 0;
  background-size: cover;
  background-repeat: no-repeat;
  width: 150px;
  background-color: rgb(212, 209, 227);
}

.painted-finger-clue {
  position: relative;

  .active.target {
    &:after {
      visibility: hidden;
    }
  }
}

.painted-finger-clue-keys {
  display: block;
  width: 130px;
  height: 130px;

  @media (max-width: 863px) {
    display: flex;
    justify-content: center;
    margin: auto !important;
    max-width: 500px;
    text-align: center;
    width: 100%;

    &:has(> div.leftHandKey) {
      justify-content: flex-start;
    }

    &:has(> div.rightHandKey) {
      justify-content: flex-end;
    }

    div.leftHandKey {
      left: calc(125px - 65px);
    }

    div.rightHandKey {
      right: calc(125px - 65px);
    }

    div {
      height: 130px;
      position: relative;
      width: 130px;
    }
  }

  div {
    height: 100%;
    background-size: contain;
  }
}

.painted-finger-clue-container {
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;

  &.painted-one-hand {
    .painted-finger-clue-left-side {
      margin-right: 200px;
    }

    .painted-finger-clue-right-side {
      margin-left: 200px;
    }
  }

  svg {
    height: 225px;
    width: 225px;

    @include ifMaxWidth(690px) {
      height: 150px;
      width: 150px;
    }
  }
}

.chrome-os {
  .painted-finger-clue-keys {
    height: 100px;
    width: 100px;
  }
}
</style>
